import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { featuredLivestock } from '../mock';

export const FeaturedLivestock = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: '-100px' });

  return (
    <section ref={containerRef} className="py-32 bg-farm-dark">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl md:text-6xl font-serif font-bold text-farm-cream mb-6">
            Featured <span className="text-farm-gold">Livestock</span>
          </h2>
          <p className="text-xl text-farm-cream/70 max-w-2xl mx-auto">
            Meet our premium cattle and elite horses
          </p>
        </motion.div>

        {/* Dairy Cattle */}
        <div className="mb-20">
          <motion.h3
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-3xl font-serif font-bold text-farm-cream mb-10"
          >
            Premium Dairy Cattle
          </motion.h3>
          <div className="grid md:grid-cols-3 gap-8">
            {featuredLivestock.cattle.map((cattle, index) => (
              <motion.div
                key={cattle.id}
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="group"
              >
                <div className="relative overflow-hidden rounded-2xl bg-farm-brown/20 backdrop-blur-sm border border-farm-green/20 hover:border-farm-gold/50 transition-all duration-500 shadow-xl hover:shadow-farm-gold/20">
                  {/* Image */}
                  <div className="relative h-64 overflow-hidden">
                    <motion.img
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                      src={cattle.image}
                      alt={cattle.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-farm-dark via-transparent to-transparent" />
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h4 className="text-2xl font-serif font-bold text-farm-cream mb-3 group-hover:text-farm-gold transition-colors duration-300">
                      {cattle.name}
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-farm-cream/60">Breed</span>
                        <span className="text-farm-cream font-medium">{cattle.breed}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-farm-cream/60">Age</span>
                        <span className="text-farm-cream font-medium">{cattle.age}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-farm-cream/60">Production</span>
                        <span className="text-farm-gold font-medium">{cattle.production}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-farm-cream/60">Health</span>
                        <span className="text-farm-green font-medium">{cattle.health}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Horses */}
        <div>
          <motion.h3
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-3xl font-serif font-bold text-farm-cream mb-10"
          >
            Elite Horses
          </motion.h3>
          <div className="grid md:grid-cols-3 gap-8">
            {featuredLivestock.horses.map((horse, index) => (
              <motion.div
                key={horse.id}
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                whileHover={{ y: -10 }}
                className="group"
              >
                <div className="relative overflow-hidden rounded-2xl bg-farm-brown/20 backdrop-blur-sm border border-farm-green/20 hover:border-farm-gold/50 transition-all duration-500 shadow-xl hover:shadow-farm-gold/20">
                  {/* Image */}
                  <div className="relative h-64 overflow-hidden">
                    <motion.img
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                      src={horse.image}
                      alt={horse.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-farm-dark via-transparent to-transparent" />
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h4 className="text-2xl font-serif font-bold text-farm-cream mb-3 group-hover:text-farm-gold transition-colors duration-300">
                      {horse.name}
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-farm-cream/60">Breed</span>
                        <span className="text-farm-cream font-medium">{horse.breed}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-farm-cream/60">Bloodline</span>
                        <span className="text-farm-cream font-medium">{horse.bloodline}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-farm-cream/60">Achievements</span>
                        <span className="text-farm-gold font-medium text-right">{horse.achievements}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};